import dayjs from 'dayjs';

import type { NearbyPlace } from '../../../data-access/api/science/openapi/api';
import {
  AssistantChatMessage,
  ChatSuggestedEvent,
  UnifiedChatRichContextEntry,
} from '../../../data-access';
import { normalizeRichContentType } from '../RichContext/contentCardUtils';

type BlockCategory = 'schedule' | 'meals' | 'sleep' | 'routines' | 'tasks';

const ADJUSTMENT_TYPE_TO_CATEGORY: Record<string, BlockCategory> = {
  schedule: 'schedule',
  meals: 'meals',
  todo_list: 'tasks',
  routine_adjustments: 'routines',
};

const getBlockCategory = (block: ChatSuggestedEvent): BlockCategory | null => {
  if (block.action === 'setRoutine' || block.action === 'deleteRoutine') return 'routines';
  if (block.action === 'setTask' || block.action === 'deleteTask') return 'tasks';
  if (block.autoScheduleFrom?.from === 'meal') return 'meals';
  if (block.autoScheduleFrom?.from === 'sleep') return 'sleep';
  if (block.action === 'setEvent' || block.action === 'deleteEvent') return 'schedule';
  return null;
};

const getBlocksByCategory = (blocks: ChatSuggestedEvent[], category: BlockCategory) =>
  blocks.filter((block) => getBlockCategory(block) === category);

const getRichContextContentType = (entry: UnifiedChatRichContextEntry): string => {
  const content = entry.richContext?.content;
  if (!content || typeof content !== 'object') return '';

  const raw = content as unknown as Record<string, unknown>;
  return normalizeRichContentType(typeof raw.type === 'string' ? raw.type : '');
};

/** Only `find_location` cards represent calendar location enrichment. */
export const isLocationEnrichmentEntry = (entry: UnifiedChatRichContextEntry): boolean =>
  getRichContextContentType(entry) === 'find_location';

const isLocationTargetBlock = (block: ChatSuggestedEvent): boolean =>
  block.action === 'setEvent' || block.action === 'setRoutine';

export const extractNearbyPlacesFromRichContextEntry = (
  entry: UnifiedChatRichContextEntry
): NearbyPlace[] => {
  if (!isLocationEnrichmentEntry(entry)) return [];

  const content = entry.richContext?.content;
  if (!content || typeof content !== 'object') return [];

  const raw = content as unknown as Record<string, unknown>;
  const payload = raw.payload as Record<string, unknown> | undefined;
  if (!payload || !Array.isArray(payload.nearby_places)) return [];

  return payload.nearby_places as NearbyPlace[];
};

export const collectRichContextEntriesForSuggestedBlocks = (
  messages: AssistantChatMessage[],
  messageIndex: number
): UnifiedChatRichContextEntry[] => {
  const entries: UnifiedChatRichContextEntry[] = [];

  for (let i = messageIndex; i >= 0; i--) {
    const message = messages[i];
    if (message.role === 'user') break;
    if (message.richContexts?.length) {
      entries.push(...message.richContexts);
    }
  }

  return entries;
};

const parseAdjustmentSuggestionId = (
  suggestionId: string
): { type: string; index: number } | null => {
  const match = suggestionId.match(/_([a-z_]+)_(\d+)$/);
  if (!match) return null;
  return { type: match[1], index: parseInt(match[2], 10) };
};

const findQueryTargetBlock = (
  blocks: ChatSuggestedEvent[],
  entry: UnifiedChatRichContextEntry
): ChatSuggestedEvent | undefined => {
  const eligibleBlocks = blocks.filter(
    (block) =>
      isLocationTargetBlock(block) &&
      (!entry.date || dayjs(block.startTime).format('YYYY-MM-DD') === entry.date)
  );

  if (eligibleBlocks.length === 1) {
    return eligibleBlocks[0];
  }

  return undefined;
};

/** Maps each 'adjustment'-sourced richContext entry (schedule/meals/todo_list/
 * routine_adjustments, indexed by suggestionId — see parseAdjustmentSuggestionId)
 * to the suggested block it belongs to, so AssistantChatSuggestedEventBlock can
 * render it inside that block's own card instead of as a separate chat bubble. */
export const buildRichContextEntriesByEventId = (
  blocks: ChatSuggestedEvent[],
  entries: UnifiedChatRichContextEntry[]
): Record<string, UnifiedChatRichContextEntry[]> => {
  const result: Record<string, UnifiedChatRichContextEntry[]> = {};

  for (const entry of entries) {
    if (entry.source !== 'adjustment') continue;

    const adjustment = parseAdjustmentSuggestionId(entry.suggestionId);
    if (!adjustment) continue;

    const category = ADJUSTMENT_TYPE_TO_CATEGORY[adjustment.type];
    if (!category) continue;

    const block = getBlocksByCategory(blocks, category)[adjustment.index];
    if (!block) continue;

    (result[block.eventId] ??= []).push(entry);
  }

  return result;
};

export const buildNearbyPlacesByEventId = (
  blocks: ChatSuggestedEvent[],
  entries: UnifiedChatRichContextEntry[]
): Record<string, NearbyPlace[]> => {
  const result: Record<string, NearbyPlace[]> = {};

  for (const entry of entries) {
    if (!isLocationEnrichmentEntry(entry)) continue;

    const places = extractNearbyPlacesFromRichContextEntry(entry);
    if (!places.length) continue;

    const adjustment = parseAdjustmentSuggestionId(entry.suggestionId);
    if (adjustment) {
      const category = ADJUSTMENT_TYPE_TO_CATEGORY[adjustment.type];
      if (category) {
        const block = getBlocksByCategory(blocks, category)[adjustment.index];
        if (block && isLocationTargetBlock(block)) {
          if (!result[block.eventId]) {
            result[block.eventId] = places;
          }
        }
        continue;
      }
    }

    if (entry.source === 'query') {
      const target = findQueryTargetBlock(blocks, entry);
      if (target && !result[target.eventId]) {
        result[target.eventId] = places;
      }
    }
  }

  return result;
};
